import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Seo from '../seo/Seo';
import { ArrowLeft } from 'lucide-react';

// Re-using the BlogPostItem interface from BlogPage (ideally this would be in a shared types file)
interface BlogPostItem {
  slug: string;
  title: string;
  content: string; 
  imageUrl?: string;
  createdAt: string;
  metaTitle?: string;
  metaDescription?: string;
}

const BlogPostDetailsPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          throw new Error("API URL is not defined.");
        }
        const response = await fetch(`${apiUrl}/posts/${slug}`);
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("Blog post not found.");
          } else {
            throw new Error(`Failed to fetch post: ${response.status} ${response.statusText}`);
          }
        }
        const data = await response.json();
        setPost(data); 
      } catch (err) {
        setError(err instanceof Error ? err.message : "An unknown error occurred");
        console.error(`Error fetching post ${slug}:`, err);
      }
      setLoading(false);
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center text-center text-gray-300 px-4 py-12">
        <p className="text-2xl font-medium animate-pulse">Loading post...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-xl mx-auto text-center bg-gray-800 p-10 rounded-xl shadow-xl border border-red-600/70">
          <p className="text-2xl text-red-400 mb-6">Error: {error}</p>
          <Link to="/blog" className="inline-flex items-center text-sky-400 hover:text-sky-300 transition-colors duration-200 group text-lg">
            <ArrowLeft className="mr-2 h-5 w-5 transition-transform duration-200 group-hover:-translate-x-1" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center px-4 py-12">
        <div className="max-w-xl mx-auto text-center bg-gray-800 p-10 rounded-xl shadow-xl">
          <p className="text-2xl text-gray-400 mb-6">Blog post not found.</p>
          <Link to="/blog" className="inline-flex items-center text-sky-400 hover:text-sky-300 transition-colors duration-200 group text-lg">
            <ArrowLeft className="mr-2 h-5 w-5 transition-transform duration-200 group-hover:-translate-x-1" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // For security, if content is HTML, consider sanitizing it or using a safe rendering method.
  // For now, assuming content is plain text or markdown that needs processing.
  // If it's markdown, you'd use a library like react-markdown here.
  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 py-16 sm:py-20 lg:py-24">
      <Seo
        title={`${post.metaTitle || post.title} - Kult.io Blog`}
        description={post.metaDescription || `${post.title} — from the Kult.io blog.`}
        path={`/blog/${post.slug}`}
        type="article"
      />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/blog" 
          className="mb-12 sm:mb-16 inline-flex items-center text-sky-400 hover:text-sky-300 transition-colors duration-200 group text-base font-medium"
        >
          <ArrowLeft className="mr-2.5 h-5 w-5 transition-transform duration-200 group-hover:-translate-x-1" />
          Back to Blog
        </Link>

        <article className="bg-gray-800/70 p-6 sm:p-8 md:p-10 rounded-xl shadow-xl border border-gray-700/80">
          {post.imageUrl && (
            <img 
              src={post.imageUrl} 
              alt={post.title} 
              className="w-full h-auto max-h-[500px] object-cover rounded-lg shadow-lg mb-10 sm:mb-12 border border-gray-600/50"
            />
          )}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 sm:mb-10 leading-tight tracking-tight">
            {post.title}
          </h1>
          <p className="text-gray-400 text-base mb-10 sm:mb-12">
            Published on: {new Date(post.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <div 
            className="prose prose-invert prose-lg text-gray-200 
                       prose-headings:text-sky-400 prose-headings:font-semibold prose-headings:mt-10 prose-headings:mb-5
                       prose-p:leading-relaxed prose-p:mb-6
                       prose-a:text-sky-400 hover:prose-a:text-sky-300 prose-a:font-medium prose-a:transition-colors prose-a:duration-200 prose-a:no-underline hover:prose-a:underline
                       prose-strong:text-gray-50 
                       prose-code:bg-gray-700/60 prose-code:p-1 prose-code:px-2 prose-code:rounded-md prose-code:text-sm prose-code:font-mono
                       prose-img:rounded-lg prose-img:shadow-md prose-img:my-10 prose-img:border prose-img:border-gray-700/70
                       prose-blockquote:border-l-4 prose-blockquote:border-sky-500 prose-blockquote:text-gray-300 prose-blockquote:italic prose-blockquote:pl-6 prose-blockquote:pr-4 prose-blockquote:py-3 prose-blockquote:my-8
                       prose-ul:list-disc prose-ul:pl-6 prose-ul:my-6 prose-li:my-2.5 prose-li:marker:text-sky-400
                       prose-ol:list-decimal prose-ol:pl-6 prose-ol:my-6 prose-li:my-2.5 prose-li:marker:text-sky-400"
            dangerouslySetInnerHTML={{ __html: post.content }} 
          />
        </article>
      </div>
    </div>
  );
};

export default BlogPostDetailsPage; 